import type { ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from 'react';
import { Icon } from '../../icons/Icon';
import type { IconName } from '../../icons/registry';
import { Spinner } from '../../shadcn/spinner';
import styles from './Button.module.css';

/*
 * The seven §5 button families, as a value first and a type second. Deriving the type from the array
 * (rather than declaring both) means the runtime list and the compile-time union cannot drift —
 * which matters because the interaction-matrix suite and the tests below enumerate the runtime
 * list to prove every variant is covered.
 */
export const BUTTON_VARIANTS = [
    'primary',
    'secondary',
    'positive',
    'danger',
    'danger-outline',
    'gold-outline',
    'ghost-icon',
] as const;
export type ButtonVariant = (typeof BUTTON_VARIANTS)[number];

export const BUTTON_SIZES = ['sm', 'md', 'lg'] as const;
export type ButtonSize = (typeof BUTTON_SIZES)[number];

/*
 * `className` is deliberately NOT accepted. A primitive that takes arbitrary classes is how raw
 * values re-enter a tokenised system one "just this once" at a time (FS-3 NFR-1). Callers compose by
 * wrapping this in their own layout element, which keeps the button's own surface owned here.
 *
 * `disabled` is omitted from the native attributes and re-declared below because this component
 * never renders the bare attribute — see the note on `aria-disabled` in the component body.
 */
type NativeButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'disabled'>;

interface ButtonCommonProps extends NativeButtonProps {
    readonly size?: ButtonSize;
    /**
     * Waiting on something (§5's Busy row, Rev 83): shadcn's `Spinner` replaces the leading icon when there
     * is one, so the box keeps its width, and otherwise leads the label in the flex row with the row's gap,
     * as shadcn's own Button shows it, so the box grows by the spinner and the gap while it waits.
     * Activation is suppressed, which is what stops the double-submit the row exists to prevent.
     */
    readonly busy?: boolean;
    /** Rendered as `aria-disabled`, never as the bare attribute — see the component body. */
    readonly disabled?: boolean;
    /** A leading glyph by concept key. Replaced by the spinner while `busy`. */
    readonly icon?: IconName;
    /**
     * The test hook (`coding-standards.md §4`), forwarded to the real `<button>`.
     *
     * `| undefined` explicitly, on wave C's arrival: Modal and DropdownMenu derive the hook they
     * pass from their OWN optional one (`${testId}-close`), so they hand this prop a
     * possibly-undefined value rather than omitting the key — and `exactOptionalPropertyTypes`
     * treats those as different things. Same widening, and the same reason, as `Input`'s.
     */
    readonly 'data-testid'?: string | undefined;
    /**
     * The real `<button>`, for a caller that has to move focus to it — the login page hands focus
     * here when the control the user pressed leaves the page (BUG-056). React 19 passes `ref` to a
     * function component as an ordinary prop, so no `forwardRef` is needed. `| undefined` lets a
     * wrapper forward its own optional ref under `exactOptionalPropertyTypes` (none does yet).
     */
    readonly ref?: Ref<HTMLButtonElement> | undefined;
}

/** Every family except `ghost-icon` carries a visible text label, and it is required. */
interface LabelledButtonProps extends ButtonCommonProps {
    readonly variant?: Exclude<ButtonVariant, 'ghost-icon'>;
    readonly children: ReactNode;
}

/**
 * `ghost-icon` is the icon-only family, so §5's "Icon-only buttons carry `aria-label`" becomes a
 * type obligation rather than a review note: the icon is required, the accessible name is required,
 * and `children` is forbidden so a stray text child cannot quietly turn it back into a labelled
 * button with the wrong padding.
 */
interface IconOnlyButtonProps extends ButtonCommonProps {
    readonly variant: 'ghost-icon';
    readonly icon: IconName;
    readonly 'aria-label': string;
    readonly children?: never;
}

export type ButtonProps = LabelledButtonProps | IconOnlyButtonProps;

/**
 * The leading slot: a spinner while busy, otherwise the optional concept icon, otherwise nothing.
 *
 * The spinner is shadcn's `Spinner` at the icon's size, in the flex row before the label, as shadcn's
 * Button shows it (design-system Rev 83): in the icon's place when there is one, otherwise ahead of the
 * label with the row's gap. It is drawn only (`aria-hidden`): `aria-busy` on the button announces the wait.
 */
function LeadingSlot({ busy, icon }: { readonly busy: boolean; readonly icon: IconName | undefined }) {
    if (busy) {
        return <Spinner aria-hidden="true" className={styles.spinner} data-slot="button-spinner" />;
    }
    return icon === undefined ? null : <Icon emphasis="regular" name={icon} size="sm" />;
}

/**
 * A real `<button>` (FS-3 FR-C2's native-first posture), styled entirely from the §2–§4 tokens.
 *
 * **Disabled is `aria-disabled`, never the bare `disabled` attribute** (§5's interaction matrix and
 * §9's permission rule). A natively disabled control drops out of the tab order, so a keyboard user
 * cannot reach it to discover *why* it is unavailable — which is the whole point of the permission
 * rule that FS-6's PermissionGuard will build on. The cost of that choice is that the browser no
 * longer suppresses activation for us, so the guard in `handleClick` is load-bearing rather than
 * defensive: without it an "unavailable" button still fires.
 *
 * The visual states key off the same ARIA attributes the accessibility tree reads
 * (`[aria-disabled='true']`, `[aria-busy='true']` in the stylesheet) rather than off classes of
 * their own. That is deliberate: it makes it impossible to render a control that *looks* disabled
 * but is not announced as such, which is the usual way these two drift apart.
 */
export function Button(props: ButtonProps) {
    const {
        variant = 'primary',
        size = 'md',
        busy = false,
        disabled = false,
        icon,
        type = 'button',
        children,
        onClick,
        'data-testid': testId,
        'aria-disabled': ariaDisabled,
        ref,
        ...rest
    } = props;

    /*
     * `aria-disabled` is MERGED rather than clobbered, and a caller that sets it wins.
     *
     * The explicit attributes below are deliberately written after the spread so they cannot be
     * dropped — but written naively that also silently discards an `aria-disabled` a caller passed,
     * which is the opposite failure and a quieter one. `PermissionGuard` is exactly that caller: it
     * marks a control unavailable by cloning the ARIA attribute onto it, because it accepts any
     * element and cannot assume a `disabled` prop exists. Found at wave D, when a guarded Button
     * announced itself as available.
     *
     * The two sources mean slightly different things and both are honoured: `disabled` is this
     * component's own state (and drives `inert` below, so the click is refused), while an incoming
     * `aria-disabled` is an outside authority saying the control may not be used. The guard blocks
     * activation itself in the capture phase, so the click guard is not the load-bearing half there.
     */
    const announcedDisabled = disabled || ariaDisabled === true || ariaDisabled === 'true';

    const inert = disabled || busy;

    function handleClick(event: MouseEvent<HTMLButtonElement>) {
        if (inert) {
            event.preventDefault();
            return;
        }
        onClick?.(event);
    }

    return (
        <button
            {...rest}
            // Explicit rather than spread-through: the spread cannot be relied on to carry these,
            // and `check-testid-coverage.mjs` cannot see through a spread either — an interactive
            // element whose only hook arrives via `...rest` reads to the gate as unaddressable.
            aria-busy={busy}
            aria-disabled={announcedDisabled}
            className={`${styles.button} ${styles[variant]} ${styles[size]}`}
            data-testid={testId}
            onClick={handleClick}
            ref={ref}
            type={type}
        >
            <LeadingSlot busy={busy} icon={icon} />
            {children}
        </button>
    );
}
